import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { ColumnDef, ColumnOrderState } from '@tanstack/react-table';
import { useQueryParams } from '../../queryParams/useQueryParams';

export const useColumnOrder: <RowData>(
  columns: ColumnDef<RowData, any>[],
  persistColumnStyle: boolean,
) => [
  ColumnOrderState,
  Dispatch<SetStateAction<ColumnOrderState>>,
  ColumnOrderState,
] = <RowData>(
  columns: ColumnDef<RowData, any>[],
  persistColumnStyle: boolean,
) => {
  const [queryParams] = useQueryParams();

  const computeDefaultOrder = useCallback(
    (cols: ColumnDef<RowData, any>[]): ColumnOrderState =>
      cols.map(
        (col) =>
          col.id ??
          ('accessorKey' in col &&
          typeof col.accessorKey === 'string' &&
          col.accessorKey
            ? col.accessorKey.replace('.', '_')
            : undefined) ??
          (typeof col.header === 'string'
            ? col.header
            : throwMissingColumnIdError(col)),
      ),
    [],
  );

  const defaultColumnOrder = useMemo(
    () => computeDefaultOrder(columns),
    [columns, computeDefaultOrder],
  );

  const columnOrderFromQuery = useMemo<ColumnOrderState | undefined>(() => {
    if (!persistColumnStyle) {
      return undefined;
    }
    const v = queryParams.columnOrder?.trim();
    return v ? v.split(',') : undefined;
  }, [persistColumnStyle, queryParams.columnOrder]);

  const [customColumnOrder, setCustomColumnOrder] = useState<
    ColumnOrderState | undefined
  >(undefined);

  const existingIds = useMemo(
    () => new Set(defaultColumnOrder),
    [defaultColumnOrder],
  );
  const sanitizeToExisting = useCallback(
    (order: ColumnOrderState): ColumnOrderState => {
      const filtered = order.filter((id) => existingIds.has(id));
      const missing = defaultColumnOrder.filter((id) => !filtered.includes(id));
      return [...filtered, ...missing];
    },
    [defaultColumnOrder, existingIds],
  );

  const columnOrder = useMemo<ColumnOrderState>(() => {
    const base =
      customColumnOrder ?? columnOrderFromQuery ?? defaultColumnOrder;
    return sanitizeToExisting(base);
  }, [
    customColumnOrder,
    columnOrderFromQuery,
    defaultColumnOrder,
    sanitizeToExisting,
  ]);

  const setColumnOrder: Dispatch<SetStateAction<ColumnOrderState>> =
    useCallback(
      (next) => {
        setCustomColumnOrder((prev) => {
          if (typeof next === 'function') {
            const seed = prev ?? columnOrder;
            return (next as (prev: ColumnOrderState) => ColumnOrderState)(seed);
          }
          return next;
        });
      },
      [columnOrder],
    );

  return [columnOrder, setColumnOrder, defaultColumnOrder];
};

const throwMissingColumnIdError = (col: ColumnDef<any>): never => {
  throw new Error(
    'accessorFn' in col
      ? `Columns require an id when using an accessorFn`
      : `Columns require an id when using a non-string header`,
  );
};
